import { shallowEqual } from 'react-redux';
import { useAppSelector } from 'state/store-hooks';
import { formatBalance } from '@pano-project/utils/formatBalance';
import {
  embeddedBalance,
  embeddedBalanceDisplay,
  embeddedBaseCurrency,
  embeddedCurrency,
  embeddedLayout,
  embeddedPaymentTypeDisplay,
  embeddedShowBalance,
} from '@pano-project/variables/boot-header-settings';
import {
  isHeaderElementVisible,
  resolveBalanceDisplay,
  resolvePaymentTypeDisplay,
  NO_PAYMENT_METHODS_PAGE,
} from '@pano-project/variables/header-element-display';
import { isNoPaymentMethodsResult } from '@pano-project/utils/isNoPaymentMethodsResult';
import { hasBalance } from 'utils/helper-functions/balance';

export interface HeaderElementsInput {
  currentPage?: string | null;
  isSecondaryStep?: boolean;
  ignorePageRestriction?: boolean;
}

export interface HeaderElements {
  showPaymentType: boolean;
  showBalance: boolean;
  balance?: string;
}

export const useHeaderElements = ({
  currentPage,
  isSecondaryStep,
  ignorePageRestriction = false,
}: HeaderElementsInput = {}): HeaderElements => {
  const {
    paymentTypeDisplay,
    balanceDisplay,
    showBalanceSetting,
    layout,
    balance,
    balanceCurrency,
    baseCurrency,
    resultPageType,
  } = useAppSelector((state: any) => {
    const customizationSettings = state.initTheme?.theme_editor_settings?.customization_settings;
    const profileInfo = state.profileInfo || {};
    const profileBaseCurrency =
      profileInfo.available_currencies?.base_currency || profileInfo.base_currency;
    return {
      paymentTypeDisplay: customizationSettings?.payment_type_display,
      balanceDisplay: customizationSettings?.balance_display,
      showBalanceSetting: customizationSettings?.show_balance,
      layout: customizationSettings?.layout,
      balance: state.initTheme?.sessionInformation?.balance,
      balanceCurrency: state.initTheme?.sessionInformation?.currency || profileBaseCurrency,
      baseCurrency: profileBaseCurrency,
      resultPageType: state.resultOfPay?.resultPageType,
    };
  }, shallowEqual);

  const resolvedPaymentTypeDisplay = resolvePaymentTypeDisplay(
    paymentTypeDisplay ?? embeddedPaymentTypeDisplay,
  );

  const resolvedBalanceDisplay = resolveBalanceDisplay({
    balanceDisplay: balanceDisplay ?? embeddedBalanceDisplay,
    showBalance: showBalanceSetting ?? embeddedShowBalance,
    layout: layout ?? embeddedLayout,
  });

  const resolvedCurrentPage =
    currentPage === 'resultPage' && isNoPaymentMethodsResult({ resultPageType })
      ? NO_PAYMENT_METHODS_PAGE
      : currentPage;

  const isVisibleOnPage = (display: ReturnType<typeof resolvePaymentTypeDisplay>): boolean =>
    display !== 'hidden' &&
    (ignorePageRestriction ||
      isHeaderElementVisible({ display, currentPage: resolvedCurrentPage, isSecondaryStep }));

  const showBalance = isVisibleOnPage(resolvedBalanceDisplay);

  const resolvedBalance = balance ?? embeddedBalance;
  const resolvedCurrency = balanceCurrency ?? embeddedCurrency;
  const resolvedBaseCurrency = baseCurrency ?? embeddedBaseCurrency;

  return {
    showPaymentType: isVisibleOnPage(resolvedPaymentTypeDisplay),
    showBalance,
    // A zero balance is a balance and is shown ("€0.00"); only a missing one is not.
    balance:
      showBalance && hasBalance(resolvedBalance)
        ? formatBalance(resolvedBalance, resolvedCurrency, resolvedBaseCurrency) || undefined
        : undefined,
  };
};
